// =============================================================================
// LocationsTab — flip booking_enabled / prefix / display name per location
// =============================================================================
// Lightweight per-row editor. Each location is independent so we save card-
// by-card (rather than the bulk "Opslaan" of the other tabs) — clearer error
// reporting and matches the natural "I'm enabling Amsterdam right now"
// workflow. fn_admin_edit_location refuses to flip booking_enabled=true while
// the prefix is still the placeholder XXX, so the disable→prefix→enable
// sequence is the only way to bring a new venue online.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { formatError } from '../../lib/errors';
import {
  listAdminLocations,
  editLocation,
  listRooms,
  editRoomCapacity,
  type AdminLocationRow,
  type AdminRoomRow,
} from '../../lib/setup';
import { locationDeepLink } from '../../lib/widget-links';
import { terminalListReaders, type TerminalReader } from '../../api/terminal';

interface Draft {
  // The server-side snapshot used for the "dirty?" comparison + as the
  // optimistic fallback if a save fails.
  snapshot:             AdminLocationRow;
  display_name:         string;       // bound to <input>, empty = clear back to NULL
  booking_number_prefix: string;
  slug:                 string;       // widget deep-link handle, empty = clear to NULL
  booking_enabled:      boolean;
  booking_listed:       boolean;      // shown in the public widget picker
  // Customer-facing card image + address (empty input = clear back to NULL).
  image_url:            string;
  address_line:         string;
  postal_code:          string;
  city:                 string;
  // Stripe Terminal reader for in-store POS payments ('' = none).
  pos_reader_id:        string;
  // Review links used by the post-visit review mail ('' = clear to NULL).
  google_review_url:      string;
  tripadvisor_review_url: string;
  tripadvisor_review_url_en: string;
  submitting:           boolean;
  error:                string | null;
  savedFlash:           boolean;
  copiedFlash:          boolean;      // "✓ Gekopieerd" flash on the bypass link
}

function snapshotToDraft(row: AdminLocationRow): Draft {
  return {
    snapshot:             row,
    display_name:         row.booking_display_name ?? '',
    booking_number_prefix: row.booking_number_prefix ?? '',
    slug:                 row.slug ?? '',
    booking_enabled:      row.booking_enabled,
    booking_listed:       row.booking_listed,
    image_url:            row.booking_image_url ?? '',
    address_line:         row.address_line ?? '',
    postal_code:          row.postal_code ?? '',
    city:                 row.city ?? '',
    pos_reader_id:        row.pos_reader_id ?? '',
    google_review_url:      row.google_review_url ?? '',
    tripadvisor_review_url: row.tripadvisor_review_url ?? '',
    tripadvisor_review_url_en: row.tripadvisor_review_url_en ?? '',
    submitting:           false,
    error:                null,
    savedFlash:           false,
    copiedFlash:          false,
  };
}

function isDirty(d: Draft): boolean {
  const s = d.snapshot;
  const currentDisplay = s.booking_display_name ?? '';
  return (
    d.display_name.trim() !== currentDisplay.trim() ||
    d.booking_number_prefix.trim().toUpperCase() !==
      (s.booking_number_prefix ?? '').toUpperCase() ||
    d.slug.trim() !== (s.slug ?? '').trim() ||
    d.booking_enabled !== s.booking_enabled ||
    d.booking_listed !== s.booking_listed ||
    d.image_url.trim() !== (s.booking_image_url ?? '').trim() ||
    d.address_line.trim() !== (s.address_line ?? '').trim() ||
    d.postal_code.trim() !== (s.postal_code ?? '').trim() ||
    d.city.trim() !== (s.city ?? '').trim() ||
    d.pos_reader_id !== (s.pos_reader_id ?? '') ||
    d.google_review_url.trim() !== (s.google_review_url ?? '').trim() ||
    d.tripadvisor_review_url.trim() !== (s.tripadvisor_review_url ?? '').trim() ||
    d.tripadvisor_review_url_en.trim() !== (s.tripadvisor_review_url_en ?? '').trim()
  );
}

// ---- Per-room capacity editing --------------------------------------------
// Rooms hang off a location (infra.rooms.location), so each location card lists
// its room(s) with an editable max_capacity. Capacity is the room's base/default
// — schedule overrides still layer on top per (room, date). `capacity` stays a
// string while editing so an in-progress empty input doesn't snap to NaN/0.
interface RoomDraft {
  snapshot:   AdminRoomRow;
  capacity:   string;
  submitting: boolean;
  error:      string | null;
  savedFlash: boolean;
}

function roomToDraft(row: AdminRoomRow): RoomDraft {
  return {
    snapshot:   row,
    capacity:   String(row.max_capacity),
    submitting: false,
    error:      null,
    savedFlash: false,
  };
}

// Dirty only when the trimmed input parses to a valid 1..100 integer that
// differs from what's stored — keeps Save disabled for empty / out-of-range.
function isRoomDirty(d: RoomDraft): boolean {
  const trimmed = d.capacity.trim();
  if (!/^\d+$/.test(trimmed)) return false;
  const n = Number(trimmed);
  return n >= 1 && n <= 100 && n !== d.snapshot.max_capacity;
}

export function LocationsTab() {
  const { operator } = useAuth();

  const [drafts, setDrafts]   = useState<Draft[]>([]);
  const [roomDrafts, setRoomDrafts] = useState<Record<string, RoomDraft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  // Stripe Terminal readers of the account (for the per-location POS dropdown).
  // Loaded best-effort: a Stripe hiccup must not block the rest of the tab.
  const [readers, setReaders] = useState<TerminalReader[]>([]);
  const [readersError, setReadersError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, rooms] = await Promise.all([listAdminLocations(), listRooms()]);
      setDrafts(rows.map(snapshotToDraft));
      setRoomDrafts(
        Object.fromEntries(rooms.map((r) => [r.id, roomToDraft(r)])),
      );
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await terminalListReaders();
      if (cancelled) return;
      if (res.ok && res.data) {
        setReaders(res.data.readers);
        setReadersError(null);
      } else {
        setReadersError(res.error?.message ?? res.error?.error ?? 'Pinterminals ophalen mislukt');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateDraft = (id: string, patch: Partial<Draft>) => {
    setDrafts((prev) =>
      prev.map((d) => (d.snapshot.id === id ? { ...d, ...patch } : d)),
    );
  };

  const updateRoomDraft = (id: string, patch: Partial<RoomDraft>) => {
    setRoomDrafts((prev) =>
      prev[id] ? { ...prev, [id]: { ...prev[id], ...patch } } : prev,
    );
  };

  const handleSaveRoom = useCallback(
    async (roomId: string) => {
      if (!operator?.id) return;
      const rd = roomDrafts[roomId];
      if (!rd || rd.submitting || !isRoomDirty(rd)) return;

      const next = Number(rd.capacity.trim());
      updateRoomDraft(roomId, { submitting: true, error: null });
      try {
        const saved = await editRoomCapacity(operator.id, roomId, next);
        setRoomDrafts((prev) =>
          prev[roomId]
            ? {
                ...prev,
                [roomId]: {
                  ...prev[roomId],
                  snapshot:   { ...prev[roomId].snapshot, max_capacity: saved },
                  capacity:   String(saved),
                  submitting: false,
                  error:      null,
                  savedFlash: true,
                },
              }
            : prev,
        );
        setTimeout(() => updateRoomDraft(roomId, { savedFlash: false }), 2000);
      } catch (e) {
        updateRoomDraft(roomId, { submitting: false, error: formatError(e) });
      }
    },
    [operator?.id, roomDrafts],
  );

  const handleSave = useCallback(
    async (id: string) => {
      if (!operator?.id) return;
      const draft = drafts.find((d) => d.snapshot.id === id);
      if (!draft || draft.submitting) return;

      updateDraft(id, { submitting: true, error: null });
      try {
        await editLocation(operator.id, {
          locationId:             draft.snapshot.id,
          booking_enabled:        draft.booking_enabled,
          booking_listed:         draft.booking_listed,
          booking_number_prefix:  draft.booking_number_prefix.trim().toUpperCase(),
          // Empty input clears back to NULL on the server.
          booking_display_name:   draft.display_name.trim(),
          booking_image_url:      draft.image_url.trim(),
          address_line:           draft.address_line.trim(),
          postal_code:            draft.postal_code.trim(),
          city:                   draft.city.trim(),
          // Server normalizes (lowercases/hyphenates) + dedupes; we send raw.
          slug:                   draft.slug.trim(),
          // POS reader: '' clears (server also clears the label).
          pos_reader_id:          draft.pos_reader_id,
          pos_reader_label:       draft.pos_reader_id
            ? (readers.find((r) => r.id === draft.pos_reader_id)?.label ?? draft.pos_reader_id)
            : '',
          // Empty clears; anything non-empty must be http(s) (server-checked).
          google_review_url:      draft.google_review_url.trim(),
          tripadvisor_review_url: draft.tripadvisor_review_url.trim(),
          tripadvisor_review_url_en: draft.tripadvisor_review_url_en.trim(),
        });
        // Re-fetch so the snapshot reflects what's actually in the DB now
        // (catches prefix normalization, etc.).
        const rows = await listAdminLocations();
        const refreshed = rows.find((r) => r.id === id);
        if (refreshed) {
          setDrafts((prev) =>
            prev.map((d) =>
              d.snapshot.id === id
                ? {
                    ...snapshotToDraft(refreshed),
                    savedFlash: true,
                  }
                : d,
            ),
          );
          setTimeout(
            () => updateDraft(id, { savedFlash: false }),
            2000,
          );
        }
      } catch (e) {
        updateDraft(id, { submitting: false, error: formatError(e) });
      }
    },
    [operator?.id, drafts, readers],
  );

  const handleCopyLink = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard blocked (insecure context / denied permission) — still flash
      // so staff know to copy manually from the visible, selectable field.
    }
    updateDraft(id, { copiedFlash: true });
    setTimeout(() => updateDraft(id, { copiedFlash: false }), 2000);
  };

  if (loading) return <div className="muted">Laden…</div>;
  if (error) return <div className="error-card">{error}</div>;

  return (
    <div className="setup-locations">
      <p className="muted setup-schedule-hint">
        Beheer welke locaties zichtbaar zijn in de boekingswidget. Zet eerst het
        prefix (bv. EHV / AMS) — dat verschijnt als prefix op iedere boekingscode.
        Pas daarna kun je een locatie bookable maken.
      </p>

      {drafts.length === 0 ? (
        <p className="muted setup-rules-empty">Nog geen locaties.</p>
      ) : null}

      {drafts.map((d) => {
        const dirty = isDirty(d);
        const locationRooms = Object.values(roomDrafts)
          .filter((r) => r.snapshot.location_id === d.snapshot.id)
          .sort((a, b) =>
            (a.snapshot.name ?? '').localeCompare(b.snapshot.name ?? ''),
          );
        return (
          <div key={d.snapshot.id} className="setup-rule-card">
            <div className="setup-rule-card-head">
              <strong>{d.snapshot.name}</strong>
              <span className="muted">
                {d.snapshot.country_code} · {d.snapshot.currency.toUpperCase()} ·{' '}
                {Number(d.snapshot.vat_rate_pct).toFixed(0)}% BTW
              </span>
            </div>

            <div className="walkin-row">
              <label className="walkin-field">
                <span>Klantnaam</span>
                <input
                  type="text"
                  value={d.display_name}
                  onChange={(e) =>
                    updateDraft(d.snapshot.id, { display_name: e.target.value })
                  }
                  placeholder={d.snapshot.name}
                />
              </label>
              <label className="walkin-field">
                <span>Boekingscode-prefix</span>
                <input
                  type="text"
                  value={d.booking_number_prefix}
                  onChange={(e) =>
                    updateDraft(d.snapshot.id, {
                      // Force uppercase + alphanumeric only as you type.
                      booking_number_prefix: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, '')
                        .slice(0, 10),
                    })
                  }
                  placeholder="EHV"
                />
              </label>
            </div>

            <label className="walkin-field" style={{ marginTop: 8 }}>
              <span>Widget-link slug (deep-link)</span>
              <input
                type="text"
                value={d.slug}
                onChange={(e) =>
                  updateDraft(d.snapshot.id, {
                    // Soft-normalize as you type; the server lowercases,
                    // hyphenates + dedupes definitively on save.
                    slug: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]+/g, '-')
                      .slice(0, 60),
                  })
                }
                placeholder="amsterdam"
              />
            </label>
            {d.slug.trim() && !(d.booking_enabled && !d.booking_listed) ? (
              <span
                className="muted"
                style={{ display: 'block', marginTop: 4, fontSize: 12 }}
              >
                Directe link: {locationDeepLink(d.slug.trim())}
              </span>
            ) : null}

            <label className="walkin-field" style={{ marginTop: 8 }}>
              <span>Afbeelding-URL (widget-stadkaart)</span>
              <input
                type="url"
                value={d.image_url}
                onChange={(e) =>
                  updateDraft(d.snapshot.id, { image_url: e.target.value })
                }
                placeholder="https://…"
              />
            </label>
            {d.image_url.trim() ? (
              <img
                className="setup-location-image-preview"
                src={d.image_url}
                alt=""
              />
            ) : null}

            <label className="walkin-field" style={{ marginTop: 8 }}>
              <span>Adres</span>
              <input
                type="text"
                value={d.address_line}
                onChange={(e) =>
                  updateDraft(d.snapshot.id, { address_line: e.target.value })
                }
                placeholder="Heuvelgalerie 102"
              />
            </label>

            <div className="walkin-row" style={{ marginTop: 8 }}>
              <label className="walkin-field">
                <span>Google review-link</span>
                <input
                  type="url"
                  value={d.google_review_url}
                  onChange={(e) =>
                    updateDraft(d.snapshot.id, { google_review_url: e.target.value })
                  }
                  placeholder="https://g.page/r/…/review"
                />
                <small className="muted">
                  Token <code>{'{{google review}}'}</code> in de reviewmail.
                </small>
              </label>
              <label className="walkin-field">
                <span>Tripadvisor review-link (NL)</span>
                <input
                  type="url"
                  value={d.tripadvisor_review_url}
                  onChange={(e) =>
                    updateDraft(d.snapshot.id, { tripadvisor_review_url: e.target.value })
                  }
                  placeholder="https://www.tripadvisor.nl/UserReviewEdit-…"
                />
                <small className="muted">
                  Token <code>{'{{trip review}}'}</code> in de reviewmail.
                </small>
              </label>
              <label className="walkin-field">
                <span>Tripadvisor review-link (EN, optioneel)</span>
                <input
                  type="url"
                  value={d.tripadvisor_review_url_en}
                  onChange={(e) =>
                    updateDraft(d.snapshot.id, { tripadvisor_review_url_en: e.target.value })
                  }
                  placeholder="https://www.tripadvisor.com/UserReviewEdit-…"
                />
                <small className="muted">
                  Dezelfde pagina op <code>.com</code>. Tripadvisor kiest de taal op domein en
                  stuurt niet door, dus zonder deze krijgt een Engelstalige klant een
                  Nederlands formulier. Leeg = iedereen krijgt de NL-link.
                </small>
              </label>
            </div>
            <p className="muted small">
              Elke vestiging is een eigen vermelding op allebei de sites, dus beide links
              staan per vestiging. Leeg laten mag: dat token blijft dan leeg, dus haal die
              knop ook uit de mail. De mail linkt niet rechtstreeks hierheen maar via
              manage.neoseum.com, zodat we zien wie erop klikt en die persoon daarna niet
              nog eens om een review vragen.
            </p>
            <div className="walkin-row">
              <label className="walkin-field">
                <span>Postcode</span>
                <input
                  type="text"
                  value={d.postal_code}
                  onChange={(e) =>
                    updateDraft(d.snapshot.id, { postal_code: e.target.value })
                  }
                  placeholder="5611 DK"
                />
              </label>
              <label className="walkin-field">
                <span>Stad</span>
                <input
                  type="text"
                  value={d.city}
                  onChange={(e) =>
                    updateDraft(d.snapshot.id, { city: e.target.value })
                  }
                  placeholder="Eindhoven"
                />
              </label>
            </div>

            {/* POS: the WisePOS reader used for walk-ins ("Pinterminal (POS)"),
                pay-and-check-in on scan, and the kiosk fallback. Readers are
                registered in the Stripe Dashboard (Terminal → Readers). */}
            <label className="walkin-field" style={{ marginTop: 8 }}>
              <span>Pinterminal (Stripe Terminal)</span>
              <select
                value={d.pos_reader_id}
                onChange={(e) =>
                  updateDraft(d.snapshot.id, { pos_reader_id: e.target.value })
                }
              >
                <option value="">— geen pinterminal —</option>
                {d.pos_reader_id && !readers.some((r) => r.id === d.pos_reader_id) ? (
                  <option value={d.pos_reader_id}>
                    {d.snapshot.pos_reader_label ?? d.pos_reader_id} (niet gevonden in Stripe)
                  </option>
                ) : null}
                {readers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label ?? r.id} · {r.status === 'online' ? 'online' : 'offline'}
                    {r.serial_number ? ` · ${r.serial_number}` : ''}
                  </option>
                ))}
              </select>
            </label>
            {readersError ? (
              <span className="muted" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                Pinterminals ophalen mislukt: {readersError}
              </span>
            ) : null}

            <label className="setup-weekday-pill" style={{ marginTop: 8 }}>
              <input
                type="checkbox"
                checked={d.booking_enabled}
                onChange={(e) =>
                  updateDraft(d.snapshot.id, {
                    booking_enabled: e.target.checked,
                  })
                }
              />
              <span>Boekbaar</span>
            </label>

            {/* Hidden = bookable but not shown in the public widget picker;
                only reachable via the ?location= bypass link below. Only
                meaningful once the location is bookable. */}
            <label
              className="setup-weekday-pill"
              style={{ marginTop: 8, opacity: d.booking_enabled ? 1 : 0.5 }}
            >
              <input
                type="checkbox"
                checked={!d.booking_listed}
                disabled={!d.booking_enabled}
                onChange={(e) =>
                  updateDraft(d.snapshot.id, {
                    booking_listed: !e.target.checked,
                  })
                }
              />
              <span>Verborgen — alleen via directe link</span>
            </label>

            {d.booking_enabled && !d.booking_listed ? (
              <div className="setup-bypass-link" style={{ marginTop: 8 }}>
                <span
                  className="muted"
                  style={{ display: 'block', fontSize: 12, marginBottom: 4 }}
                >
                  Verborgen locatie — deel deze directe boekingslink:
                </span>
                <div
                  style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                >
                  <input
                    type="text"
                    readOnly
                    value={locationDeepLink(d.slug.trim() || d.snapshot.id)}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{ flex: 1, fontSize: 12 }}
                    aria-label="Directe boekingslink"
                  />
                  <button
                    type="button"
                    className="walkin-primary-btn"
                    onClick={() =>
                      void handleCopyLink(
                        d.snapshot.id,
                        locationDeepLink(d.slug.trim() || d.snapshot.id),
                      )
                    }
                  >
                    {d.copiedFlash ? '✓ Gekopieerd' : 'Kopieer'}
                  </button>
                </div>
              </div>
            ) : null}

            {locationRooms.length > 0 ? (
              <div className="setup-location-rooms">
                <div className="setup-location-rooms-title">
                  Capaciteit per ruimte
                </div>
                {locationRooms.map((rd) => (
                  <div
                    key={rd.snapshot.id}
                    className="setup-room-capacity-row"
                  >
                    <span className="setup-room-capacity-name">
                      {rd.snapshot.name ?? 'Ruimte'}
                    </span>
                    <input
                      className="setup-room-capacity-input"
                      type="number"
                      min={1}
                      max={100}
                      value={rd.capacity}
                      onChange={(e) =>
                        updateRoomDraft(rd.snapshot.id, {
                          capacity: e.target.value,
                        })
                      }
                      aria-label={`Capaciteit ${rd.snapshot.name ?? 'ruimte'}`}
                    />
                    <button
                      className="walkin-primary-btn setup-room-capacity-save"
                      onClick={() => void handleSaveRoom(rd.snapshot.id)}
                      disabled={
                        !isRoomDirty(rd) || rd.submitting || !operator?.id
                      }
                    >
                      {rd.submitting
                        ? 'Bezig…'
                        : rd.savedFlash
                          ? '✓'
                          : 'Opslaan'}
                    </button>
                    {rd.error ? (
                      <div className="error-card walkin-submit-error setup-room-capacity-error">
                        {rd.error}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {d.error ? (
              <div className="error-card walkin-submit-error">{d.error}</div>
            ) : null}

            <div className="walkin-step-actions">
              <span className="muted">{d.savedFlash ? '✓ Opgeslagen' : null}</span>
              <button
                className="walkin-primary-btn"
                onClick={() => void handleSave(d.snapshot.id)}
                disabled={!dirty || d.submitting || !operator?.id}
              >
                {d.submitting ? 'Bezig…' : 'Opslaan'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
